import type { Dayjs } from 'dayjs';
import type { CSSProperties, ReactNode } from 'react';

import type { CATEGORY_TYPE } from './categoryConst';
import type { LANGUAGE, SCHEDULE_PERIOD, WEEKDAY } from './commonConst';

enum SOCKET_SYNC_TYPE {
  INVALID,
  CREATE,
  UPDATE,
  RESET,
  DELETE,
}

export { SOCKET_SYNC_TYPE };
declare global {
  // 특정 필드를 optional하게 만드는 유틸리티 타입
  type Alias = string;

  type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

  type PriorityKeyT = 1 | 2 | 3 | 4 | 5;
  type RegionT = {
    code: string;
    country: string;
    lang: string;
    in_feedback: boolean;
  };

  type LanguageT = {
    code: LANGUAGE;
    name: string;
    in_feedback: boolean;
  };

  type RegionMapT = Record<string, RegionT>;

  type KeywordT = {
    key: string;
    name: string;
    count?: number;
  };

  type CustomFieldsT = {
    key: string; // key to be matched with columnKey in DataTableColumnBaseT
    type: CustomFieldTypeT;
    value: string | number | number[];
  };

  type CustomFieldTypeT = 1 | 2 | 3 | 4 | 5;
  // 1: 'text', 2: 'number', 3: 'select', 4: 'multi-select', 5: 'date'

  type CustomFieldOptionValueT = {
    key: number;
    name: string;
    order?: number;
  };

  type PaginationInfoT = {
    count: number; // total count
    page: number; // requested page number (from 1 ~)
    size: number; // count per page
  };

  type CommonListItemT<V, N> = {
    value: V;
    name: N;
  };

  type StoreSetter<State> = <
    A extends
      | string
      | {
          type: unknown;
        },
  >(
    partial:
      | State
      | Partial<State>
      | ((state: State) => State | Partial<State>),
    replace?: boolean | undefined,
    action?: A | undefined,
  ) => void;

  type PaginationCursorItemT = {
    alias: string;
    sortKey: any;
  };

  type PaginationCursorsT = {
    first: PaginationCursorItemT | null;
    last: PaginationCursorItemT | null;
  };

  type PaginationCursorsTV2 = {
    first: string | null;
    last: string | null;
  };

  // 0: INVALID 1: CREATE 2: UPDATE 3: RESET 4: DELETE

  type SOCKET_SYNC_STRUCT<T extends {}> = {
    type: SOCKET_SYNC_TYPE;
    alias: string;
    field: keyof T;
    payload: T;
  };

  type VIEW_CATEGORY =
    | 'feedback_view'
    | 'account_view'
    | 'contact_view'
    | 'agent_view';

  type PosOptionT = {
    offset?: {
      x?: number;
      y?: number;
    };
    reflect?: {
      x?: boolean;
      y?: boolean;
    };
  };

  type RadioItemT = {
    key: StringNumberValue;
    node: ReactNode;
  };

  type PaginationQueriesT = {
    cursor?: string;
    next?: 1 | -1;
    sortby?: string;
    sortkey?: string;
    order?: 'desc' | 'asc';
  };

  type AliasNamePair = {
    alias: string;
    name: string;
  };

  type AliasTitlePair = {
    alias: string;
    title: string;
  };

  type PostBookmarkParams = {
    link: string;
    name: string;
  };

  type BookmarkT = PostBookmarkParams & {
    alias: string;
  };

  type BookmarkStatusT = 'add' | 'edit' | 'delete' | undefined;

  type StatusT = 1 | 2 | 3 | 4 | 5;

  type QueryObj = Record<string, string | number | null>;

  type QueryT = {
    key: string;
    value: string | null;
  };

  type IconValueT = {
    value: string;
    icon: ReactNode;
  };

  type PaginatedData<T> = T & {
    paginate_token?: string;
  };

  type StyledCss = ReturnType<typeof import('styled-components').css>;

  type ModalToggleStatusT<T = undefined> = {
    status: boolean;
    pos?: CSSProperties;
    modalState?: T;
  };

  type DateRangeT = {
    start_date: DateString;
    end_date: DateString;
  };

  type NullableDateRangeT = {
    start_date: DateString | null;
    end_date: DateString | null;
  };

  type DayJsDateRangeT = {
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  };

  type NullableDayJsDateRangeT = {
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  };

  type SizeType = 'xs' | 's' | 'm' | 'l' | 'xl';

  type SelectOne<T, Keys extends keyof T = keyof T> = Keys extends keyof T
    ? {
        [K in Keys]-?: Required<Pick<T, K>> &
          Partial<Record<Exclude<Keys, K>, undefined>>;
      }
    : never;

  /**
   * @description
   * YYYY-MM-DD format
   */
  type DateString = string;

  /**
   * @description
   * RFC 3339 date string
   */
  type RFCDateString = string;

  /**
   * @description
   * value1,value2,value3 <- AND
   * value1|value2|value3 <- OR
   */
  type QueryString = string;

  type NumRange<
    N extends number,
    Result extends Array<unknown> = [],
  > = Result['length'] extends N
    ? Result['length']
    : Result['length'] | NumRange<N, [...Result, unknown]>;

  type Hour = NumRange<23>;

  type KeyLabelSetFilter = {
    set: Record<string, string>;
    label: Record<string, string[]>;
  };

  type ValueLabelT = {
    value: StringNumberValue;
    label: string;
  };

  type Progress = {
    total: number;
    current: number;
  };

  type FetchCountRes = {
    count: number;
  };

  type ResponseWithCount<T> = T & {
    count: number;
  };

  type CountByDateT = {
    date: string;
    count: number;
  };

  type StatisticDataT = {
    total: number;
    buckets: DataBucketT[];
  };

  type DataBucketT = {
    bucket: StringNumberValue;
    count: number;
  };

  type ClassificationItemT = {
    id: CATEGORY_TYPE;
    color: string;
    name: string;
  };

  type RangeValue = {
    min?: number;
    max?: number;
  };

  interface Schedule {
    /**
     * @description
     * 스크래핑 시작 시간 (0-23)
     */
    hour: number;
    period: SCHEDULE_PERIOD;
    /**
     * @description
     * period = WEEKLY인 경우에만 설정
     * 0 : sunday / 1 : monday
     */
    weekday?: WEEKDAY;
    /**
     * @description
     * period = MONTHLY인 경우에만 설정
     * 1 : 1st day / 2 : 2nd day / ... / 28th day
     */
    date?: number;
  }
}
